import React, { useCallback, useContext, useEffect, useReducer, useRef, useState } from "react";
import { graphql, usePaginationFragment } from "react-relay";

import { compact } from "lodash";

import { ReactComponent as DarkIcon } from "~/activityStream/empty-state-icon-dark.svg";
import { ReactComponent as LightIcon } from "~/activityStream/empty-state-icon.svg";
import AppContext from "~/common/AppContext";
import LoadMoreButton from "~/common/LoadMoreButton";
import ThemeContext from "~/common/ThemeContext";
import { useLazyLoadQuery } from "~/lib/relayUtil";
import WebsocketClient from "~/lib/websocket";
import type { ObjectiveAttributes } from "~/okrs/Objective";
import AddNewUpdate from "~/okrs/Updates/AddNewUpdate";
import Update from "~/okrs/Updates/Update";
import type { SelectorOption } from "~/ui/selectors/Selector";

import type { UpdatesPaginationQuery as UpdatesPaginationQueryType } from "~/__generated__/UpdatesPaginationQuery.graphql";
import type { UpdatesQuery as UpdatesQueryType } from "~/__generated__/UpdatesQuery.graphql";
import type { UpdatesViewer$key } from "~/__generated__/UpdatesViewer.graphql";

import styles from "./Updates.module.css";

const updatesQuery = graphql`
	query UpdatesQuery($objectiveId: ID) {
		viewer {
			...UpdatesViewer
		}
	}
`;

const updatesPaginationQuery = graphql`
	fragment UpdatesViewer on Viewer
	@argumentDefinitions(
		before: { type: "String" }
		after: { type: "String" }
		first: { type: "Int" }
		last: { type: "Int", defaultValue: 50 }
	)
	@refetchable(queryName: "UpdatesPaginationQuery") {
		id
		updates: activities(outerObjectId: $objectiveId, before: $before, after: $after, first: $first, last: $last)
			@connection(key: "updates_updates") {
			edges {
				node {
					id
					__typename
					actor {
						id
						fullName
						image
						email
					}
					verb
					createdAt
					cache
					data

					... on ObjectiveAssignedActivity {
						assignee: indirectObject {
							__typename
							id
							firstName
							lastName
							fullName
						}
					}

					... on KeyResultAssignedActivity {
						assignee: indirectObject {
							__typename
							id
							firstName
							lastName
							fullName
						}

						keyResult: object {
							__typename
							id
							title
						}
					}

					... on ObjectiveAssignedTeamActivity {
						assigneeTeam: indirectObject {
							__typename
							id
							title
						}
					}

					... on KeyResultAssignedTeamActivity {
						assigneeTeam: indirectObject {
							__typename
							id
							title
						}

						keyResult: object {
							__typename
							id
							title
						}
					}

					... on KeyResultStartValueChangeActivity {
						keyResult: object {
							__typename
							id
							title
						}
					}

					... on KeyResultCurrentValueChangeActivity {
						keyResult: object {
							__typename
							id
							title
						}
					}

					... on KeyResultTargetValueChangeActivity {
						keyResult: object {
							__typename
							id
							title
						}
					}

					... on KeyResultDueDateChangeActivity {
						keyResult: object {
							__typename
							id
							title
						}
					}

					... on KeyResultDeletedActivity {
						keyResult: object {
							__typename
							id
							title
						}
					}

					... on KeyResultUpdatedActivity {
						keyResult: object {
							__typename
							id
							title
						}
					}

					... on ObjectiveCreatedFloatingDescription {
						objective: outerObject {
							__typename
							title
							url
						}
					}

					... on KeyResultCreatedFloatingDescription {
						keyResult: object {
							__typename
							id
							title
						}
					}
				}
			}
		}
	}
`;

export interface OkrSelectorOption {
	label: string;
	value: string;
	isObjective?: boolean;
}

interface UpdatesProps {
	objectiveId?: string;
	objectiveTitle?: string;
	contributors: ObjectiveAttributes["contributors"];
	hasAccess?: boolean;
	keyResults: ObjectiveAttributes["keyResults"];
	isAddingDescription: boolean;
	setIsAddingDescription: (isAddingDescription: boolean) => void;
	selectedOkr: SelectorOption;
	setSelectedOkr: (selectedOkr: SelectorOption) => void;
}

export default function Updates({
	objectiveId,
	objectiveTitle,
	hasAccess,
	contributors,
	keyResults,
	isAddingDescription,
	setIsAddingDescription,
	selectedOkr,
	setSelectedOkr,
}: UpdatesProps) {
	const { isDarkMode } = useContext(ThemeContext);
	const { user } = useContext(AppContext);
	const listRef = useRef<HTMLDivElement>(null);
	const [, forceUpdate] = useReducer(x => x + 1, 0);
	const { viewer } = useLazyLoadQuery<UpdatesQueryType>(
		updatesQuery,
		{ objectiveId },
		{ fetchPolicy: "store-and-network" },
	);
	const { data, loadPrevious, hasPrevious, refetch } = usePaginationFragment<
		UpdatesPaginationQueryType,
		UpdatesViewer$key
	>(updatesPaginationQuery, viewer);

	const {
		updates: { edges: readonlyActivities },
	} = data;
	const updates = compact(readonlyActivities);
	updates.reverse();

	const [filteredOkr, setFilteredOkr] = useState<OkrSelectorOption>({
		label: objectiveTitle ?? "",
		value: objectiveId ?? "",
		isObjective: true,
	});

	const onSocketMessage = useCallback(
		(payload: { data: { verb: string } }) => {
			let { verb } = payload.data;
			if (!verb) {
				return;
			}
			verb = verb.toLowerCase();
			if (!verb.includes("objective") && !verb.includes("key_result")) {
				return;
			}
			refetch({ objectiveId }, { fetchPolicy: "store-and-network" });
		},
		[refetch, objectiveId],
	);

	useEffect(() => {
		const socket = WebsocketClient.getInstance().getOrJoinGroup(`activities.${user?.id}`.replace(/=/g, ""), {
			userId: user?.id,
		});
		socket.on("message", onSocketMessage);
		if (listRef.current) {
			forceUpdate();
		}
		return () => {
			if (socket) {
				socket.off("message", onSocketMessage);
				socket.close();
			}
		};
	}, [listRef, user, onSocketMessage]);

	const loadMore = useCallback(
		(callback: () => void) => {
			if (hasPrevious) {
				loadPrevious(50, { onComplete: callback });
			}
		},
		[hasPrevious, loadPrevious],
	);

	const filteredUpdates = updates.filter(u => u.node.keyResult?.id === filteredOkr.value || filteredOkr.isObjective);

	let updatesContent;

	if (updates.length > 0) {
		if (filteredUpdates.length > 0) {
			updatesContent = filteredUpdates.map(update => (
				<Update key={update.node.id} contributors={contributors} update={update.node} hasAccess={hasAccess} />
			));
		} else {
			updatesContent = (
				<div className={styles.keyResultNoActivity}>
					No activity yet on <b>{filteredOkr.label}</b>
				</div>
			);
		}
	} else {
		updatesContent = (
			<div className={styles.emptyStateContainer}>
				{isDarkMode ? <DarkIcon /> : <LightIcon />}
				<p className={styles.emptyStateText}>Updates to the objective and its key results will be displayed here</p>
			</div>
		);
	}

	return (
		<section>
			{objectiveId && objectiveTitle && hasAccess && (
				<AddNewUpdate
					contributors={contributors}
					objectiveId={objectiveId}
					objectiveTitle={objectiveTitle}
					keyResults={keyResults}
					viewerId={data.id}
					isAddingDescription={isAddingDescription}
					setIsAddingDescription={setIsAddingDescription}
					selectedOkr={selectedOkr}
					setSelectedOkr={setSelectedOkr}
					filteredOkr={filteredOkr}
					setFilteredOkr={setFilteredOkr}
				/>
			)}
			<div className={styles.updates} ref={listRef}>
				{updatesContent}
			</div>
			{listRef.current && hasPrevious && (
				<div className={styles.loadMoreContainer}>
					<LoadMoreButton
						dotted={false}
						fireOnMount={true}
						disabled={!hasPrevious}
						loadMore={loadMore}
						context={listRef.current}
						debounceTimeout={50}
					/>
				</div>
			)}
		</section>
	);
}
